import * as React from "react";
import styled from "styled-components";
import { useCallback, useState, useContext, useRef } from "react";
import { isPermitted } from "util/PermissionsMixin";
import { Button, ButtonGroup, MenuItem } from "components/bootstrap";
import { Icon, ShareButton } from "components/common";
import UserNotification from "util/UserNotification";
import View from "views/logic/views/View";
import onSaveView from "views/logic/views/OnSaveViewAction";
import ViewLoaderContext from "views/logic/ViewLoaderContext";
import NewViewLoaderContext from "views/logic/NewViewLoaderContext";
import ExportModal from "views/components/export/ExportModal";
import EntityShareModal from "components/permissions/EntityShareModal";
import useCurrentUser from "hooks/useCurrentUser";
import * as ViewsPermissions from "views/Permissions";
import ViewPropertiesModal from "views/components/dashboard/DashboardPropertiesModal";
import { loadAsDashboard, loadNewSearch } from "views/logic/views/Actions";
import IfPermitted from "components/common/IfPermitted";
import { executePluggableSearchDuplicationHandler as executePluggableDuplicationHandler } from "views/logic/views/pluggableSaveViewFormHandler";
import useSaveViewFormControls from "views/hooks/useSaveViewFormControls";
import useIsDirty from "views/hooks/useIsDirty";
import useIsNew from "views/hooks/useIsNew";
import useView from "views/hooks/useView";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { loadView, updateView } from "views/logic/slices/viewSlice";
import useHistory from "routing/useHistory";
import usePluginEntities from "hooks/usePluginEntities";
import SavedSearchesModal from "views/components/searchbar/saved-search/SavedSearchesModal";
import SaveViewButton from "views/components/searchbar/SaveViewButton";
import EntityShareDomain from "domainActions/permissions/EntityShareDomain";
import useHotkey from "hooks/useHotkey";
import { createGRN } from "logic/permissions/GRN";
import useSelectedStreamsGRN from "views/hooks/useSelectedStreamsGRN";
import { createView, deleteView } from "views/api/views";
import { MoreActionsMenu } from "components/common/MoreActions";
import SavedSearchForm from "./SavedSearchForm";
const Container = styled(ButtonGroup)`
  display: flex;
  justify-content: flex-end;
`;
const _isAllowedToEdit = (view, currentUser) => view.owner === (currentUser == null ? void 0 : currentUser.username) || isPermitted(currentUser == null ? void 0 : currentUser.permissions, [ViewsPermissions.View.Edit(view.id)]);
const _extractErrorMessage = (error) => error && error.additional && error.additional.body && error.additional.body.message ? error.additional.body.message : error;
const usePluggableSearchAction = (loaded, view) => {
  const modalRefs = useRef({});
  const pluggableSearchActions = usePluginEntities("views.components.searchActions");
  const actions = pluggableSearchActions.filter((perspective) => perspective.useCondition ? !!perspective.useCondition() : true).map(({ component: PluggableSearchAction, key, modals }) => {
    if (modals) {
      const refs = Object.fromEntries(
        modals.map(({ key: modalKey }) => modalKey).map((mKey) => [mKey, () => modalRefs.current[mKey]])
      );
      return /* @__PURE__ */ React.createElement(PluggableSearchAction, { key, loaded, search: view, modalRefs: refs });
    }
    return /* @__PURE__ */ React.createElement(PluggableSearchAction, { key, loaded, search: view });
  });
  const actionModals = pluggableSearchActions.filter(({ modals }) => !!modals).flatMap(({ modals }) => modals).map(({ key, component: ActionModal }) => /* @__PURE__ */ React.createElement(
    ActionModal,
    {
      key,
      search: view,
      ref: (r) => {
        modalRefs.current[key] = r;
      }
    }
  ));
  return { actions, actionModals };
};
const SearchActionsMenu = () => {
  var _a;
  const dirty = useIsDirty();
  const view = useView();
  const isNew = useIsNew();
  const viewLoaderFunc = useContext(ViewLoaderContext);
  const currentUser = useCurrentUser();
  const loadNewView = useContext(NewViewLoaderContext);
  const isAllowedToEdit = view && view.id && _isAllowedToEdit(view, currentUser);
  const formTarget = useRef();
  const [showForm, setShowForm] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showMetadataEdit, setShowMetadataEdit] = useState(false);
  const [showShareSearch, setShowShareSearch] = useState(false);
  const currentTitle = (_a = view == null ? void 0 : view.title) != null ? _a : "";
  const dispatch = useViewsDispatch();
  const onUpdateView = useCallback((newView) => dispatch(updateView(newView)), [dispatch]);
  const { selectedStreamsGRN } = useSelectedStreamsGRN();
  const loaded = isNew === false;
  const disableReset = !(dirty || loaded);
  const savedViewTitle = loaded ? "Saved search" : "Save search";
  const title = dirty ? "Unsaved changes" : savedViewTitle;
  const pluggableSaveViewControls = useSaveViewFormControls();
  const history = useHistory();
  const toggleFormModal = useCallback(() => setShowForm((cur) => !cur), []);
  const closeFormModal = useCallback(() => setShowForm(false), []);
  const openFormModal = useCallback(() => setShowForm(true), []);
  const toggleListModal = useCallback(() => setShowList((cur) => !cur), []);
  const toggleExport = useCallback(() => setShowExport((cur) => !cur), []);
  const toggleMetadataEdit = useCallback(() => setShowMetadataEdit((cur) => !cur), []);
  const toggleShareSearch = useCallback(() => setShowShareSearch((cur) => !cur), []);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableSearchAction(loaded, view);
  const saveSearch = useCallback(
    async (newTitle, entityShare) => {
      if (!view.id) {
        return;
      }
      const newView = view.toBuilder().title(newTitle).type(View.Type.Search).build();
      await dispatch(onSaveView(newView, entityShare));
      if (entityShare) {
        await EntityShareDomain.update("search", title, createGRN("search", view.id), entityShare);
      }
      closeFormModal();
      await dispatch(loadView(newView));
    },
    [closeFormModal, dispatch, view, title]
  );
  const saveAsSearch = useCallback(
    async (newTitle, entityShare) => {
      if (!newTitle || newTitle === "") {
        return;
      }
      const viewWithPluginData = await executePluggableDuplicationHandler(
        view,
        currentUser.permissions,
        pluggableSaveViewControls
      );
      const newView = viewWithPluginData.toBuilder().newId().title(newTitle).type(View.Type.Search).build();
      createView(newView, entityShare, view.id).then((createdView) => {
        toggleFormModal();
        return createdView;
      }).then((createdView) => {
        viewLoaderFunc(createdView.id);
      }).then(() => UserNotification.success(`Saving view "${newView.title}" was successful!`, "Success!")).catch((error) => UserNotification.error(`Saving view failed: ${_extractErrorMessage(error)}`, "Error!"));
    },
    [currentUser.permissions, pluggableSaveViewControls, toggleFormModal, view, viewLoaderFunc]
  );
  const deleteSavedSearch = useCallback(
    (deletedView) => deleteView(deletedView).then(
      () => UserNotification.success(`Deleting saved search "${deletedView.title}" was successful!`, "Success!")
    ).then(() => {
      if (deletedView.id === view.id) {
        loadNewSearch(history);
      }
      return Promise.resolve();
    }).catch(
      (error) => UserNotification.error(`Deleting saved search failed: ${_extractErrorMessage(error)}`, "Error!")
    ),
    [history, view.id]
  );
  const _loadAsDashboard = useCallback(() => {
    loadAsDashboard(history, view);
  }, [history, view]);
  useHotkey({
    actionKey: "save",
    callback: () => loaded ? saveSearch(title) : openFormModal(),
    scope: "search",
    dependencies: [loaded, saveSearch, title]
  });
  useHotkey({
    actionKey: "save-as",
    callback: () => openFormModal(),
    scope: "search"
  });
  return /* @__PURE__ */ React.createElement(Container, { "aria-label": "Search Meta Buttons" }, /* @__PURE__ */ React.createElement(
    SavedSearchForm,
    {
      key: currentTitle,
      show: showForm,
      saveSearch,
      saveAsSearch,
      isCreateNew: isNew || !isAllowedToEdit,
      toggleModal: toggleFormModal,
      value: currentTitle,
      selectedStreamGRN: selectedStreamsGRN,
      viewId: !isNew && view.id
    },
    /* @__PURE__ */ React.createElement(SaveViewButton, { title, ref: formTarget, onClick: toggleFormModal })
  ), /* @__PURE__ */ React.createElement(Button, { title: "Load a previously saved search", onClick: toggleListModal }, /* @__PURE__ */ React.createElement(Icon, { name: "folder", type: "regular" }), " Load"), showList && /* @__PURE__ */ React.createElement(
    SavedSearchesModal,
    {
      deleteSavedSearch,
      toggleModal: toggleListModal,
      activeSavedSearchId: view.id
    }
  ), /* @__PURE__ */ React.createElement(
    ShareButton,
    {
      entityType: "search",
      entityId: view.id,
      onClick: toggleShareSearch,
      bsStyle: "default",
      disabledInfo: isNew && "Only saved searches can be shared."
    }
  ), /* @__PURE__ */ React.createElement(MoreActionsMenu, { "aria-label": "Open search actions dropdown", id: "search-actions-dropdown", pullRight: true, solid: true }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: toggleMetadataEdit, disabled: !isAllowedToEdit, icon: "edit" }, "Edit metadata"), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "dashboards:create" }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: _loadAsDashboard, icon: "dashboard" }, "Export to dashboard")), /* @__PURE__ */ React.createElement(MenuItem, { onSelect: toggleExport, icon: "download" }, "Export"), /* @__PURE__ */ React.createElement(MenuItem, { disabled: disableReset, onSelect: loadNewView, icon: "restart_alt" }, "Reset search"), pluggableActions.length ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), pluggableActions) : null), showExport && /* @__PURE__ */ React.createElement(ExportModal, { view, closeModal: toggleExport }), showMetadataEdit && /* @__PURE__ */ React.createElement(
    ViewPropertiesModal,
    {
      show: true,
      view,
      title: "Editing saved search",
      submitButtonText: "Update search",
      onClose: toggleMetadataEdit,
      onSave: onUpdateView
    }
  ), showShareSearch && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: view.id,
      entityType: "search",
      entityTitle: view.title,
      description: "Search for a User or Team to add as collaborator on this saved search.",
      onClose: toggleShareSearch
    }
  ), pluggableActionModals);
};
export default SearchActionsMenu;
